import { supabase } from "src/utils/supabaseClient";
import exception from "@/utils/exception";
import Constants from "src/utils/constants";
import Helper from "@/utils/helper";

// At the module level, add a cache for count results
const countCache = new Map();

// Function to create a cache key based on query parameters
function createCacheKey(tableName, fetchDeleted, searchTxt, queryParams) {
  return JSON.stringify({
    table: tableName,
    deleted: fetchDeleted,
    search: searchTxt,
    params: queryParams
  });
}

/**
 * Inserts a new record into the specified table.
 *
 * @param {string} tableName - The name of the table to insert data into
 * @param {Object} obj - The object containing the data to insert
 *
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - error: Error message if any, or null if successful
 *   - id: The ID of the newly inserted record, or null if insertion failed
 *
 * @example
 * const result = await insertData('users', {
 *   name: 'John Doe',
 *   email: 'john@example.com'
 * });
 */
async function insertData(tableName, obj) {
  try {
    const { data, error } = await supabase.from(tableName).insert(obj).select();
    if (error) {
      return {
        error: error.message,
        userError: error?.code
          ? Helper.mapDatabaseErrorsToUserMessages(error)
          : null,
        id: null
      };
    }
    return {
      error: null,
      id: data && data.length > 0 ? data[0].id : null, // Adjust according to your table's schema
      data: data && data.length > 0 ? data[0] : null
    };
  } catch (error) {
    exception.catchException(error);
    return error;
  }
}

/**
 * Updates an existing record in the specified table.
 *
 * @param {string} tableName - The name of the table to update data in
 * @param {Object} obj - The object containing the data to update, must include an 'id' field
 *
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - error: Error message if any, or null if successful
 *   - data: The updated record, or null if update failed
 *
 * @example
 * const result = await updateData('users', {
 *   id: 123,
 *   name: 'John Smith',
 *   email: 'john.smith@example.com'
 * });
 */
async function updateData(tableName, obj, { syncStamp = true } = {}) {
  try {
    if (syncStamp) obj.local_updated_at = new Date().toISOString();
    const { data, error } = await supabase
      .from(tableName)
      .update(obj)
      .eq("id", obj.id)
      .select();

    return {
      error: (error && error.message) || null,
      errorCode: (error && error.code) || null,
      userError: error?.code
        ? Helper.mapDatabaseErrorsToUserMessages(error)
        : null,
      data: data && data.length > 0 ? data[0] : null
    };
  } catch (error) {
    exception.catchException(error);
    return error;
  }
}

/**
 * Fetches data from a specified table with filtering, sorting, and pagination capabilities.
 * Implements optimized count caching to reduce redundant database queries.
 *
 * @param {string} tableName - The name of the table to fetch data from
 * @param {boolean} [fetchDeleted=false] - Whether to include soft-deleted records (is_deleted=true)
 * @param {string|null} [searchTxt=null] - Text to search across all columns (ilike %text%)
 * @param {string|string[]|null} [sortField=null] - Column(s) to sort by; can be a single string or array of strings
 * @param {number|number[]} [sortOrder=0] - Direction to sort (0=none, 1=ascending, -1=descending);
 *                                             can be a single value or array matching sortField
 * @param {number} [page=0] - Zero-based page number for pagination
 * @param {number} [pageSize=10] - Number of records per page
 * @param {boolean} [fetchAll=false] - If true, ignores pagination and returns all records
 * @param {Array<{field: string, value: any, opType?: string}>} [queryParams=[]] - Advanced filtering parameters:
 *   - field: The column name to filter on
 *   - value: The value to filter by
 *   - opType: Optional comparison type from Constants.enums.COMPARISON_TYPE (IS, CS, or default=EQ)
 * @param {boolean} [headOnly=false] - When true, only returns the count of matching records (for internal use)
 * @param {string} [select="*"] - PostgREST select string, for fixed column
 *   lists or joined tables (e.g. `*, tags!inner(tag)`). The count query uses
 *   it too, so filters on a joined table apply to the count.
 *
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - data: Array of records matching the query
 *   - totalItems: Total count of records matching the query (before pagination)
 *   - error: Error message if any, or null if successful
 *
 * @example
 * // Basic pagination with sorting
 * const result = await fetchData('users', false, null, 'created_at', -1, 0, 20);
 *
 * @example
 * // Advanced filtering with queryParams
 * const result = await fetchData('flra_forms', false, null, 'date', -1, 0, 10, false, [
 *   { field: 'status', value: 3 },
 *   { field: 'company', value: 'LPCrane' }
 * ]);
 *
 * @notes
 * - The function implements count caching to avoid redundant database queries
 * - Cache is keyed by tableName + fetchDeleted + searchTxt + queryParams
 * - Cache expires after 60 seconds
 * - For efficient pagination, count queries only execute when filters change or cache expires
 */
async function fetchData(
  tableName,
  fetchDeleted = false,
  searchTxt = null,
  sortField = null, // Can be a single string or an array of strings
  sortOrder = 0, // Can be a single value (0 = none, 1 = asc, -1 = desc) or an array of directions
  page = 0,
  pageSize = 10,
  fetchAll = false,
  queryParams = [], // New parameter for additional filters
  headOnly = false, // New parameter to only return count
  select = "*"
) {
  try {
    // Initial query setup
    let query = supabase.from(tableName);

    // Use different select options based on headOnly flag
    if (headOnly) {
      query = query.select(select === "*" ? "id" : select, {
        count: "exact",
        head: true
      });
    } else {
      query = query.select(select, { count: "exact" });
    }

    // Apply filter for deleted items
    if (!fetchDeleted) {
      query = query.eq("is_deleted", false);
    }

    // Apply search text to all columns if provided
    if (searchTxt) {
      let _res = await getColumnNames(tableName);
      const searchableColumns = _res.data || [];

      // Split columns into text search vector columns and regular columns
      const tsvColumns = [];
      const regularColumns = [];

      searchableColumns.forEach((col) => {
        if (col.includes("tsv")) {
          tsvColumns.push(col);
        } else {
          regularColumns.push(col);
        }
      });

      if (tsvColumns.length > 0) {
        // Process search text for tsquery format.
        // Split by spaces, add a ':*' prefix wildcard to every token (words and
        // numbers alike, so partial matches work — e.g. defect/highway numbers),
        // then join with the AND operator. Each token is stripped of tsquery
        // syntax characters (`. - : & | ! ( ) '`) first, otherwise to_tsquery
        // throws a syntax error on pasted input.
        const processedSearchTxt = searchTxt
          .split(" ")
          .map((word) => word.replace(/[.:&|!()'*-]/g, "").trim())
          .filter((word) => word !== "") // Remove empty strings
          .map((word) => `${word}:*`) // Add wildcard to each token
          .join(" & "); // Join with AND operator

        if (processedSearchTxt) {
          query = query.textSearch(tsvColumns, processedSearchTxt, {
            config: "english"
          });
        }
      } else {
        const searchFilters = regularColumns
          .map((col) => `${col}.ilike.%${searchTxt}%`)
          .join(",");
        query = query.or(searchFilters);
      }
    }

    // Apply additional filters from params object
    if (queryParams && Array.isArray(queryParams)) {
      queryParams.forEach(({ field, value, opType }) => {
        if (value === undefined) {
          value = "";
        }
        // Apply the appropriate comparison type
        switch (opType) {
          // Check if the field's value exactly matches the given value
          case Constants.enums.COMPARISON_TYPE.IS_LIKE:
            // Keep `field` flexible (can be a jsonPath-style column reference).
            query = query.filter(field, "ilike", `%${value}%`);
            break;
          // Check if the field's value exactly matches the given value
          case Constants.enums.COMPARISON_TYPE.IS:
            query = query.is(field, value);
            break;
          case Constants.enums.COMPARISON_TYPE.IS_NOT_NULL:
            query = query.not(field, "is", null);
            break;
          // Check if the field contains the given value (array search)
          case Constants.enums.COMPARISON_TYPE.CS:
            query = query.contains(field, `{"${value}"}`);
            break;
          case Constants.enums.COMPARISON_TYPE.JSON_CS:
            query = query.contains(field, value);
            break;
          case Constants.enums.COMPARISON_TYPE.OR:
            if (Array.isArray(value)) {
              // Check if the field's value matches any value in the given list
              query = query.in(field, value);
            } else {
              query = query.eq(field, value);
            }
            break;
          // Check if the field's value is different from the given value
          case Constants.enums.COMPARISON_TYPE.NOT_EQ:
            query = query.neq(field, value);
            break;
          // Check if the field's value is between the given range
          case Constants.enums.COMPARISON_TYPE.BETWEEN:
            if (value && value.from && value.to) {
              query = query.gte(field, value.from).lte(field, value.to);
            }
            break;
          // Check if the field's value is greater than the given value
          case Constants.enums.COMPARISON_TYPE.GT:
            query = query.gt(field, value);
            break;
          // Check if the field's value is less than the given value
          case Constants.enums.COMPARISON_TYPE.LT:
            query = query.lt(field, value);
            break;
          // Use exact match (eq) if no comparison type is provided
          default:
            query = query.eq(field, value);
        }
      });
    }

    // Check if we're only requesting the count
    if (headOnly) {
      const { count, error: countError } = await query;

      if (countError) {
        throw new Error(countError.message);
      }

      return count;
    }

    // Check count cache or get count if needed
    const cacheKey = createCacheKey(
      tableName,
      fetchDeleted,
      searchTxt,
      queryParams
    );
    let totalCount;

    if (countCache.has(cacheKey)) {
      totalCount = countCache.get(cacheKey);
    } else {
      // Get count using the same query but with headOnly flag
      totalCount = await fetchData(
        tableName,
        fetchDeleted,
        searchTxt,
        null, // sortField not needed for count
        0, // sortOrder not needed for count
        0, // page not needed for count
        0, // pageSize not needed for count
        false, // fetchAll not needed for count
        queryParams,
        true, // headOnly = true to get only the count
        select
      );

      // Cache the count result
      countCache.set(cacheKey, totalCount);

      // Implement cache expiration
      setTimeout(() => {
        countCache.delete(cacheKey);
      }, 60000); // Cache expires after 1 minute
    }

    // Apply sorting logic
    if (!sortField) {
      // Default sort by `created_at`
      query = query.order("created_at", { ascending: true });
    } else if (Array.isArray(sortField)) {
      // If sortField is an array, handle multiple fields sorting
      sortField.forEach((field, index) => {
        const direction = Array.isArray(sortOrder)
          ? sortOrder[index] || 0 // Get the corresponding direction or default to 0 (no sort)
          : sortOrder; // If sortOrder is not an array, use the same direction for all fields

        if (direction !== 0) {
          query = query.order(field, {
            ascending: direction === 1 // 1 = asc, -1 = desc
          });
        }
      });
    } else {
      // Single field sorting
      if (sortOrder !== 0) {
        query = query.order(sortField, {
          ascending: sortOrder === 1 // 1 = asc, -1 = desc
        });
      }
    }

    // Apply pagination only if fetchAll is false
    if (!fetchAll) {
      const from = page * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);
    }

    // Execute the query for data
    const { data, error } = await query;

    return {
      data,
      totalItems: totalCount,
      error: (error && error.message) || null
    };
  } catch (error) {
    exception.catchException(error);
    return {
      error: error.message || "An unexpected error occurred"
    };
  }
}

/**
 * Retrieves a single record from the specified table by its ID.
 *
 * @param {string} tableName - The name of the table to fetch data from
 * @param {string|number} id - The ID of the record to retrieve
 *
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - error: Error message if any, or null if successful
 *   - data: The retrieved record, or null if not found or error occurred
 *
 * @example
 * const result = await getData('users', 123);
 */
async function getData(tableName, id) {
  try {
    const { data, error } = await supabase
      .from(tableName)
      .select()
      .eq("id", id)
      .single(); // Ensure only one record is returned

    if (error) {
      return {
        error: error.message || null,
        data: null
      };
    }

    return {
      error: null,
      data
    };
  } catch (error) {
    exception.catchException(error);
    return {
      error: error.message || "An unexpected error occurred",
      data: null
    };
  }
}

/**
 * Fetches column names from a specified table using a database function.
 *
 * @param {string} table_name - The name of the table to get column names from
 *
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - data: Array of column names
 *   - error: Error message if any, or null if successful
 *
 * @example
 * const result = await getColumnNames('users');
 */
async function getColumnNames(table_name) {
  try {
    const { data, error } = await supabase.rpc(
      Constants.enums.DATABASE_FUNCTION_ENUM.GET_COLUMN_NAMES,
      { p_table_name: table_name }
    );

    return { data, error: (error && error.message) || null };
  } catch (error) {
    console.error(error);
    return [];
  }
}

/**
 * Fetches the next value from a database sequence.
 *
 * @param {string} sequenceName - The name of the sequence to get the next value from
 *
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - data: The next sequence value
 *   - error: Error message if any, or null if successful
 *
 * @example
 * const result = await getNextSequenceValue('user_id_seq');
 */
async function getNextSequenceValue(sequenceName) {
  try {
    const { data, error } = await supabase.rpc(
      Constants.enums.DATABASE_FUNCTION_ENUM.GET_NEXT_SEQUENCE_VALUE,
      { seq_name: sequenceName }
    );

    return { data, error: (error && error.message) || null };
  } catch (error) {
    exception.catchException(error);
    return {
      error: error.message || "An unexpected error occurred"
    };
  }
}

/**
 * Uploads a file to Supabase storage.
 *
 * @param {string} bucketName - The name of the storage bucket
 * @param {string} filePath - The path where the file should be stored
 * @param {File|Blob} file - The file to upload
 * @param {Object} [options={}] - Additional upload options
 * @param {string} [options.cacheControl='3600'] - Cache control header value
 *
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - data: The upload result data
 *   - error: Error message if any, or null if successful
 *
 * @example
 * const result = await uploadToStorage(
 *   'avatars',
 *   'user123/profile.jpg',
 *   fileObject,
 *   { cacheControl: '3600' }
 * );
 */
async function uploadToStorage(bucketName, filePath, file, options = {}) {
  try {
    const defaultOptions = {
      cacheControl: "3600",
      ...options
    };

    const { data, error } = await supabase.storage
      .from(bucketName)
      .upload(filePath, file, defaultOptions);

    return {
      data,
      error: error ? error.message : null
    };
  } catch (error) {
    exception.catchException(error);
    return {
      data: null,
      error: error.message || "An unexpected error occurred during upload"
    };
  }
}

/**
 * Fetches distinct values for specified columns in a table.
 *
 * @param {string} tableName - The name of the table to query
 * @param {string|string[]} columnNames - Single column name or array of column names to get distinct values for
 *
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - data: Object containing distinct values for each requested column
 *   - error: Error message if any, or null if successful
 *
 * @example
 * // Get distinct values for a single column
 * const result = await getDistinctColumnValues('users', 'status');
 *
 * @example
 * // Get distinct values for multiple columns
 * const result = await getDistinctColumnValues('users', ['status', 'role']);
 */
async function getDistinctColumnValues(tableName, columnNames) {
  try {
    const columns = Array.isArray(columnNames) ? columnNames : [columnNames];

    if (columns.length === 0) {
      throw new Error("columnNames must be a non-empty string or array");
    }

    const { data, error } = await supabase.rpc("get_distinct_column_values", {
      p_table_name: tableName,
      p_column_names: columns
    });

    // sort data by label
    data.sort((a, b) => {
      if (!a.label && !b.label) return 0;
      if (!a.label) return 1;
      if (!b.label) return -1;
      return a.label.localeCompare(b.label);
    });
    return { data, error: (error && error.message) || null };
  } catch (error) {
    exception.catchException(error);
    return {
      data: null,
      error: error.message || "An unexpected error occurred"
    };
  }
}

/**
 * Deletes data from a specified table based on provided query parameters.
 * Supports various comparison types for flexible filtering conditions.
 *
 * @param {string} tableName - The name of the table to delete data from
 * @param {Array<{field: string, value: any, opType?: string}>} queryParams - Array of filter parameters:
 *   - field: The column name to filter on
 *   - value: The value to filter by
 *   - opType: Optional comparison type from Constants.enums.COMPARISON_TYPE (IS, CS, OR, NOT_EQ, BETWEEN, GT, LT, or default=EQ)
 *
 * @returns {Promise<Object>} Returns an object with the following properties:
 *   - data: The deleted records
 *   - error: Error message if any, or null if successful
 *
 * @example
 * // Delete records where status is 'completed'
 * const result = await deleteData('tasks', [
 *   { field: 'status', value: 'completed' }
 * ]);
 *
 * @example
 * // Delete records with multiple conditions
 * const result = await deleteData('orders', [
 *   { field: 'status', value: 'cancelled', opType: 'IS' },
 *   { field: 'amount', value: { from: 100, to: 500 }, opType: 'BETWEEN' }
 * ]);
 */
async function deleteData(tableName, queryParams) {
  try {
    let query = supabase.from(tableName).delete();

    queryParams.forEach(({ field, value, opType }) => {
      if (!Helper.isValueExist(value)) {
        return;
      }
      // Apply the appropriate comparison type
      switch (opType) {
        // Check if the field's value exactly matches the given value
        case Constants.enums.COMPARISON_TYPE.IS:
          query = query.is(field, value);
          break;
        // Check if the field contains the given value (array search)
        case Constants.enums.COMPARISON_TYPE.CS:
          query = query.contains(field, `{"${value}"}`);
          break;
        case Constants.enums.COMPARISON_TYPE.JSON_CS:
          query = query.contains(field, value);
          break;
        case Constants.enums.COMPARISON_TYPE.OR:
          if (Array.isArray(value)) {
            // Check if the field's value matches any value in the given list
            query = query.in(field, value);
          } else {
            query = query.eq(field, value);
          }
          break;
        // Check if the field's value is different from the given value
        case Constants.enums.COMPARISON_TYPE.NOT_EQ:
          query = query.neq(field, value);
          break;
        // Check if the field's value is between the given range
        case Constants.enums.COMPARISON_TYPE.BETWEEN:
          if (value && value.from && value.to) {
            query = query.gte(field, value.from).lte(field, value.to);
          }
          break;
        // Check if the field's value is greater than the given value
        case Constants.enums.COMPARISON_TYPE.GT:
          query = query.gt(field, value);
          break;
        // Check if the field's value is less than the given value
        case Constants.enums.COMPARISON_TYPE.LT:
          query = query.lt(field, value);
          break;
        // Use exact match (eq) if no comparison type is provided
        default:
          query = query.eq(field, value);
      }
    });

    const { data, error } = await query;

    return { data, error: (error && error.message) || null };
  } catch (error) {
    exception.catchException(error);
    return {
      data: null,
      error: error.message || "An unexpected error occurred"
    };
  }
}

/**
 * @param {string} rpcName
 * @param {Object} params
 * @param {Object} [options]
 * @param {boolean} [options.rawError=false] - Return Supabase's own error
 *   object (message, code, details, hint) instead of only its message, for
 *   callers that classify errors by code — e.g. the form / PDF builders'
 *   version-conflict and lock-timeout handling.
 */
async function useSupabaseRPC(rpcName, params, { rawError = false } = {}) {
  try {
    const { data, error } = await supabase.rpc(rpcName, params);
    if (rawError) return { data, error: error ?? null };
    return { data, error: (error && error.message) || null };
  } catch (error) {
    exception.catchException(error);
    if (rawError) return { data: null, error };
    return {
      error: error.message || "An unexpected error occurred"
    };
  }
}

// ---------------------------------------------------------------------------
// Edge functions and auth — the client's own results, unchanged
// ---------------------------------------------------------------------------

async function invokeEdgeFunction(functionName, options) {
  return await supabase.functions.invoke(functionName, options);
}

async function signInWithOAuth(credentials) {
  return await supabase.auth.signInWithOAuth(credentials);
}

async function signInWithPassword(credentials) {
  return await supabase.auth.signInWithPassword(credentials);
}

async function signOut() {
  return await supabase.auth.signOut();
}

async function setSession(session) {
  return await supabase.auth.setSession(session);
}

async function refreshSession(currentSession) {
  return await supabase.auth.refreshSession(currentSession);
}

async function getSession() {
  return await supabase.auth.getSession();
}

async function getUser() {
  return await supabase.auth.getUser();
}

function onAuthStateChange(callback) {
  return supabase.auth.onAuthStateChange(callback);
}

/**
 * The Supabase client itself, for RxDB replication — which needs the client
 * object, not a query. Nothing else should use it.
 */
function getClient() {
  return supabase;
}

export default {
  insertData,
  fetchData,
  updateData,
  getData,
  uploadToStorage,
  getNextSequenceValue,
  getDistinctColumnValues,
  deleteData,
  useSupabaseRPC,
  invokeEdgeFunction,
  signInWithOAuth,
  signInWithPassword,
  signOut,
  setSession,
  refreshSession,
  getSession,
  getUser,
  onAuthStateChange,
  getClient
};
